import React from 'react';
import { animated, useChain, useSpringRef, useTransition, type UseTransitionProps } from 'react-spring';
import { BpDesignSystem } from '../../types';
import { Box } from '../Box/Box';

/**
 * Utility component for handling transitions.
 */

export type AnimationOptions = UseTransitionProps;

export type TransitionProps = BpDesignSystem.Props<{
  open: boolean;
  elements: React.ReactNode[];
  children: (child: React.ReactNode, index: number) => React.ReactNode;
  options?: Partial<AnimationOptions>;
}>;

export const Transition: React.FC<TransitionProps> = ({ elements, children, open, options, ...rest }) => {
  const { trail, ...state } = withDefaults(options);
  const transApi = useSpringRef();
  const transition = useTransition(open ? elements : [], {
    ref: transApi,
    trail: trail / elements.length,
    ...state
  });
  useChain([transApi], [0]);
  return (
    <Box style={{ overflow: 'hidden', position: 'relative' }} {...rest}>
      {transition((style, child, state, index) => {
        return (
          <animated.div style={{ ...style, overflow: 'hidden', position: 'relative' }}>
            {children(child, index)}
          </animated.div>
        );
      })}
    </Box>
  );
};

function withDefaults(options?: Partial<AnimationOptions>): AnimationOptions & { trail: number } {
  return {
    trail: 250,
    from: { opacity: 0, translateY: -5 },
    enter: { opacity: 1, translateY: 0 },
    leave: { opacity: 0, translateY: -5 },
    ...options
  };
}
